/**
 * sanitizePlateFragment — shared structural sanitizer for Plate node trees.
 *
 * Apply on EVERY ingress path: init value, paste (all branches), import
 * (docx/md), and programmatic insertions (AI output). This is the single
 * source of truth for the A1 taxonomy from the Plate structural audit.
 *
 * Rules enforced (A1 taxonomy):
 *
 * CRASH-CLASS:
 *  1. Element children null/undefined/non-array → [{ text: '' }]
 *  2. Bare text leaf as direct child of table/tr/td/th → wrapped up
 *  3. Void nodes (hr/image/toc/mention) → children forced to [{ text: '' }]
 *
 * SILENT-DROP / DATA-LOSS CLASS:
 *  4. tr outside table; td/th outside tr → still sanitized (parentType guard
 *     is at callers' level — within a fragment we recursively handle these)
 *  5. ul/ol/li / Slate list-item → convert to indent p (listStyleType+indent)
 *  6. h4/h5/h6 → clamped to h3; typeless+textless nodes → dropped
 *
 * VISUAL-CORRUPTION CLASS:
 *  7. Block nested inside p/heading → parent keeps only inline children; block
 *     children are lifted OUT as siblings (mirrored from usePlateEditorLogic's
 *     normalizer which handles this at runtime, but we also clean at ingress).
 *  8. listStyleType/listStart/listRestart on non-LIST_INDENT_TARGETS → stripped
 *  9. column_group nested in column → unwrapped (caller's normalizer handles at
 *     runtime; we strip them here to avoid a crash before normalization runs)
 *
 * Additionally:
 *  - Preserves the stray-newline-spacer collapse from the original init guard
 *  - Ensures every element.children is a non-empty array (min [{ text: '' }])
 *
 * This function is PURE (no Plate editor instance required). It operates on
 * arbitrary node arrays (root OR fragment level — not just root like
 * sanitizePlateValue).
 */

// Void node types: must carry children: [{ text: '' }] exactly.
const VOID_TYPES = new Set(['hr', 'image', 'toc', 'mention']);

// Heading types registered by the editor (h4/h5/h6 have no plugin → clamp).
const REGISTERED_HEADINGS = new Set(['h1', 'h2', 'h3']);
const UNREGISTERED_HEADINGS = new Set(['h4', 'h5', 'h6']);

// List container types produced by converters that use the ul/ol/li model
// (markdownToPlate flattenLists already handles these, but slateToPlate and
// raw fragment paste can still emit them).
const LIST_CONTAINER_TYPES = new Set(['ul', 'ol']);
const LIST_ITEM_TYPES = new Set(['li', 'lic', 'list-item', 'list_item']);

// Block types that may legally contain block children (container blocks).
// p / headings / blockquote / callout are content blocks that must hold only
// inline/text children — any block children of these must be lifted out.
const CONTENT_BLOCK_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'blockquote', 'callout']);

// Types on which listStyleType/listStart/listRestart are legal (the
// LIST_INDENT_TARGETS set from usePlateEditorLogic, excluding td/th per the
// intentional comment there — double-bullet-in-cells bug). Must be kept in
// sync with that set.
const LIST_INDENT_TARGETS = new Set(['p', 'h1', 'h2', 'h3', 'blockquote', 'callout']);

// Table structural types.
const TABLE_TYPE = 'table';
const TR_TYPE = 'tr';
const TD_TYPES = new Set(['td', 'th']);

// column types on which listStyleType is illegal.
const NON_LIST_COLUMN_TYPES = new Set(['column_group', 'column']);

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

/** The same stray-newline-spacer check from usePlateEditorLogic (do not diverge). */
function isStrayNewlineSpacer(children: any[]): boolean {
    if (!children.length || !children.every((c) => c && typeof c.text === 'string')) return false;
    const joined = children.map((c) => c.text).join('');
    return joined.includes('\n') && joined.trim() === '';
}

/** True for inline/text nodes (text leaves or inline elements like links/mentions). */
function isInlineOrText(node: any): boolean {
    if (!node || typeof node !== 'object') return false;
    if (typeof node.text === 'string') return true;
    // link ('a') and mention are registered as inlines.
    const t = node.type;
    return t === 'a' || t === 'mention' || t === 'mention_input' || t === 'emoji_input' || t === 'slash_input';
}

/** True for block element nodes. */
function isBlock(node: any): boolean {
    return !!(node && typeof node === 'object' && node.type && typeof node.text !== 'string' && !isInlineOrText(node));
}

// --------------------------------------------------------------------------
// ul/ol/li → indent-p flattening
// --------------------------------------------------------------------------

const LIST_TYPE_MAP: Record<string, string> = { ul: 'disc', ol: 'decimal' };

function flattenListNode(node: any, indent: number): any[] {
    const out: any[] = [];
    if (!node || typeof node !== 'object') return out;

    const type: string = node.type ?? '';

    if (LIST_CONTAINER_TYPES.has(type)) {
        const listStyleType = LIST_TYPE_MAP[type] ?? 'disc';
        for (const child of Array.isArray(node.children) ? node.children : []) {
            out.push(...flattenListItem(child, listStyleType, indent));
        }
        return out;
    }

    if (LIST_ITEM_TYPES.has(type)) {
        out.push(...flattenListItem(node, 'disc', indent));
        return out;
    }

    // Not a list node — recurse into children to flatten nested lists.
    const rawChildren = Array.isArray(node.children) ? node.children : [];
    const newChildren: any[] = [];
    const liftedSiblings: any[] = [];
    for (const child of rawChildren) {
        if (child && typeof child === 'object' && (LIST_CONTAINER_TYPES.has(child.type) || LIST_ITEM_TYPES.has(child.type))) {
            liftedSiblings.push(...flattenListNode(child, indent));
        } else {
            newChildren.push(child);
        }
    }
    out.push({ ...node, children: newChildren.length ? newChildren : [{ text: '' }] });
    out.push(...liftedSiblings);
    return out;
}

function flattenListItem(li: any, listStyleType: string, indent: number): any[] {
    if (!li || typeof li !== 'object') return [];
    const out: any[] = [];

    // lic (list item content) or regular li: extract text children, collect nested lists.
    const children: any[] = Array.isArray(li.children) ? li.children : [];
    let textChildren: any[] = [];
    const nestedLists: any[] = [];

    for (const child of children) {
        if (!child || typeof child !== 'object') continue;
        if (child.type === 'lic' || child.type === 'p') {
            textChildren = textChildren.concat(Array.isArray(child.children) ? child.children : []);
        } else if (LIST_CONTAINER_TYPES.has(child.type) || LIST_ITEM_TYPES.has(child.type)) {
            nestedLists.push(child);
        } else if (typeof child.text === 'string' || isInlineOrText(child)) {
            textChildren.push(child);
        } else {
            // Block-level child inside a list item (table, image, etc.) — lift as sibling.
            nestedLists.push(child);
        }
    }

    if (textChildren.length === 0) textChildren = [{ text: '' }];
    out.push({ type: 'p', listStyleType: li.listStyleType ?? listStyleType, indent: indent + 1, children: textChildren });

    // Recurse into nested lists.
    for (const nested of nestedLists) {
        if (LIST_CONTAINER_TYPES.has(nested.type ?? '')) {
            const nestedStyle = LIST_TYPE_MAP[nested.type] ?? listStyleType;
            for (const nestedLi of Array.isArray(nested.children) ? nested.children : []) {
                out.push(...flattenListItem(nestedLi, nestedStyle, indent + 1));
            }
        } else if (LIST_ITEM_TYPES.has(nested.type ?? '')) {
            out.push(...flattenListItem(nested, listStyleType, indent + 1));
        } else {
            // Non-list block lifted out of li (table, image, etc.) — pass through to be sanitized.
            out.push(nested);
        }
    }

    return out;
}

// --------------------------------------------------------------------------
// Core recursive sanitizer
// --------------------------------------------------------------------------

/**
 * Sanitize a Plate node array (fragment or full document value). Works at any
 * tree level — it is not limited to root children like `sanitizePlateValue`.
 *
 * @param nodes   The node array to sanitize.
 * @param parentType  The type of the containing element, if known.
 * @returns A valid Plate node array (never empty — min [{ type:'p', children:[{text:''}] }] at root, min [{text:''}] inside cells).
 */
export function sanitizePlateFragment(nodes: any, parentType?: string): any[] {
    if (!Array.isArray(nodes)) {
        // Top-level fallback: at least one paragraph.
        return [{ type: 'p', children: [{ text: '' }] }];
    }

    // Pre-pass: flatten ul/ol/li containers into indent-p siblings so the main
    // loop never sees them.
    const preFlattened: any[] = [];
    for (const node of nodes) {
        if (node && typeof node === 'object' && (LIST_CONTAINER_TYPES.has(node.type) || LIST_ITEM_TYPES.has(node.type))) {
            preFlattened.push(...flattenListNode(node, 0));
        } else {
            preFlattened.push(node);
        }
    }

    const out: any[] = [];
    for (const node of preFlattened) {
        if (node == null || typeof node !== 'object') continue;

        // ── TEXT LEAF ──────────────────────────────────────────────────────
        if (typeof node.text === 'string') {
            // Clone the leaf: passing through a shared object reference (e.g. the
            // EMPTY_EDITOR_VALUE singleton) lets two independently-mounted editors
            // hold the same leaf identity, which breaks Slate's node->path lookup
            // ("Unable to find the path for Slate node") for whichever editor loses
            // the race.
            const leaf = { ...node };
            if (parentType === TABLE_TYPE) {
                out.push({ type: TR_TYPE, children: [{ type: 'td', children: [{ type: 'p', children: [leaf] }] }] });
            } else if (parentType === TR_TYPE) {
                out.push({ type: 'td', children: [{ type: 'p', children: [leaf] }] });
            } else if (TD_TYPES.has(parentType ?? '')) {
                out.push({ type: 'p', children: [leaf] });
            } else {
                out.push(leaf);
            }
            continue;
        }

        // From here: element nodes (have a type, no text property).
        let nodeType: string | undefined = node.type;

        // ── DROP typeless+textless nodes ──────────────────────────────────
        if (!nodeType) continue;

        // ── h4/h5/h6 → h3 ────────────────────────────────────────────────
        if (UNREGISTERED_HEADINGS.has(nodeType)) {
            nodeType = 'h3';
        }

        // ── TABLE structural enforcement ──────────────────────────────────
        // table → only tr children (handled by recursive call below with parentType='table')
        // tr → only td/th children (handled by recursive call below with parentType='tr')
        if (parentType === TABLE_TYPE && nodeType !== TR_TYPE) {
            // Non-tr inside table: if it's a tr-like wrapper (thead/tbody/tfoot/
            // table-head/table-body) hoist its children as tr siblings.
            const wrapperTypes = new Set(['thead', 'tbody', 'tfoot', 'table-head', 'table-body', 'table-footer']);
            if (wrapperTypes.has(nodeType)) {
                const innerChildren = Array.isArray(node.children) ? node.children : [];
                const hoisted = sanitizePlateFragment(innerChildren, TABLE_TYPE);
                out.push(...hoisted);
            }
            // Any other non-tr inside a table is dropped (A1 rule 2/4).
            continue;
        }
        if (parentType === TR_TYPE && !TD_TYPES.has(nodeType)) {
            // Non-td/th inside a tr: drop.
            continue;
        }

        // ── VOID NODES ────────────────────────────────────────────────────
        // Spread node but always override children to the void marker AND strip
        // any list props that are illegal on void nodes.
        if (VOID_TYPES.has(nodeType)) {
            const { listStyleType: _ls, listStart: _lst, listRestart: _lr, children: _c, ...voidRest } = node as any;
            out.push({ ...voidRest, type: nodeType, children: [{ text: '' }] });
            continue;
        }

        // ── listStyleType/listStart/listRestart strip from non-targets ────
        let sanitizedNode = { ...node, type: nodeType };
        if (
            !LIST_INDENT_TARGETS.has(nodeType) &&
            (sanitizedNode.listStyleType != null || sanitizedNode.listStart != null || sanitizedNode.listRestart != null)
        ) {
            const { listStyleType: _ls, listStart: _lst, listRestart: _lr, ...rest } = sanitizedNode;
            sanitizedNode = rest;
        }

        // ── Recurse into children ─────────────────────────────────────────
        const rawChildren = Array.isArray(node.children) ? node.children : [];

        // For content blocks (p/headings/blockquote/callout): children must be
        // inline/text only. Lift block children out as siblings.
        if (CONTENT_BLOCK_TYPES.has(nodeType)) {
            const inlineChildren: any[] = [];
            const blockSiblings: any[] = [];
            for (const child of rawChildren) {
                if (child == null || typeof child !== 'object') continue;
                if (isBlock(child)) {
                    blockSiblings.push(child);
                } else if (typeof child.text === 'string') {
                    // Bare text leaf — nothing to recurse into, but still clone
                    // (see the TEXT LEAF branch above for why: a shared leaf
                    // reference across two editors breaks Slate's path lookup).
                    inlineChildren.push({ ...child });
                } else {
                    // Inline element (link/mention/mention_input/emoji_input/
                    // slash_input) — recurse through the full node sanitizer so
                    // its own children get the same VOID_TYPES/heading-clamp/
                    // listStyleType-strip treatment as any other node, instead
                    // of being pushed through unsanitized. parentType is THIS
                    // node's own type (nodeType) — the inline child's immediate
                    // container is the p/heading being processed here, not
                    // whatever contains that p/heading.
                    inlineChildren.push(...sanitizePlateFragment([child], nodeType));
                }
            }
            const cleanInline = inlineChildren.length ? inlineChildren : [{ text: '' }];
            const finalChildren = isStrayNewlineSpacer(cleanInline) ? [{ text: '' }] : cleanInline;
            out.push({ ...sanitizedNode, children: finalChildren });
            // Lifted block siblings go through the full sanitizer as top-level nodes.
            if (blockSiblings.length) {
                out.push(...sanitizePlateFragment(blockSiblings, parentType));
            }
            continue;
        }

        // td/th: must have at least one block child. Bare inline/text children
        // get wrapped in a p.
        if (TD_TYPES.has(nodeType)) {
            const cellChildren = sanitizePlateFragment(rawChildren, nodeType);
            // Ensure at least one block child.
            const hasBlock = cellChildren.some(isBlock);
            const finalCellChildren = hasBlock
                ? cellChildren
                : [{ type: 'p', children: cellChildren.length ? cellChildren : [{ text: '' }] }];
            out.push({
                ...sanitizedNode,
                children: finalCellChildren.length ? finalCellChildren : [{ type: 'p', children: [{ text: '' }] }],
            });
            continue;
        }

        // table: must have at least one tr child. null/[]/all-invalid children
        // recurse (with parentType='table') to [] per the empty-fragment rule
        // above — without this guard the generic branch below falls back to
        // children:[{text:''}], producing an invalid table (Plate requires tr
        // rows, not a text leaf). Synthesize a minimal valid skeleton instead,
        // mirroring the td/th guard's "wrap, don't drop" behaviour.
        if (nodeType === TABLE_TYPE) {
            const rowChildren = sanitizePlateFragment(rawChildren, TABLE_TYPE);
            const hasRow = rowChildren.some((c: any) => c?.type === TR_TYPE);
            const finalRowChildren = hasRow
                ? rowChildren
                : [{ type: TR_TYPE, children: [{ type: 'td', children: [{ type: 'p', children: [{ text: '' }] }] }] }];
            out.push({ ...sanitizedNode, children: finalRowChildren });
            continue;
        }

        // tr: must have at least one td/th child. null/[]/all-invalid children
        // hit the same crash-class gap as table above — the generic branch
        // would fall back to children:[{text:''}], producing a bare text leaf
        // directly under tr (invalid; Plate requires td/th rows). Synthesize
        // a minimal valid td>p skeleton instead.
        if (nodeType === TR_TYPE) {
            const cellChildrenAtTrLevel = sanitizePlateFragment(rawChildren, TR_TYPE);
            const hasCell = cellChildrenAtTrLevel.some((c: any) => TD_TYPES.has(c?.type));
            const finalCellChildrenAtTrLevel = hasCell
                ? cellChildrenAtTrLevel
                : [{ type: 'td', children: [{ type: 'p', children: [{ text: '' }] }] }];
            out.push({ ...sanitizedNode, children: finalCellChildrenAtTrLevel });
            continue;
        }

        // All other element nodes: recurse normally.
        const cleanChildren = sanitizePlateFragment(rawChildren, nodeType);
        const finalChildren = isStrayNewlineSpacer(cleanChildren) ? [{ text: '' }] : cleanChildren;
        out.push({ ...sanitizedNode, children: finalChildren.length ? finalChildren : [{ text: '' }] });
    }

    if (out.length === 0) {
        // At root level (no parentType): always return at least one paragraph.
        // Inside table/tr/td contexts: return empty — the parent call handles
        // the fallback (e.g. tr constructor, td wrapper). Returning a p here
        // would produce invalid structure inside those contexts.
        if (!parentType) {
            return [{ type: 'p', children: [{ text: '' }] }];
        }
        return [];
    }
    return out;
}
